// Hero de la home (refonte 2026), porté de src/sections/hero.js du prototype.
// Livret 3D (Three.js, chargé à la demande depuis oqp-booklet.js), changement de livret avec ‹ › qui
// recolore tout le site (--accent), clic sur le livret = sa fiche produit (comme « Découvrir »),
// titre qui rebondit façon logo DVD, logos presse en boucle derrière le livret.
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

// réglages communs aux livrets (public/booklets/oqp-n/config.json du prototype) ; chaque livret ne
// fournit que ses textures et la double page où il s'ouvre (land)
const BASE = {
  spread: null, placeholderPages: 10, background: null, autoplay: 'inview', riffle: 600, turn: 800,
  ease: [0, 0, 1, 1], extraSpreads: 4, repeatSpread: 2, paper: .57, staples: true,
  startTilt: 26, startRotate: -5, endTilt: 26, exposure: .95, keyLight: .49, fillLight: .54, lightAngle: 34,
  shadow: 0, dropShadow: 1, dropColor: '#215167', dropDistance: 3.5, dropAngle: 27, dropBlur: 0, dropSize: .5,
};

function setAccent(color) {
  if (!color) return;
  document.documentElement.style.setProperty('--accent', color);
  dispatchEvent(new CustomEvent('accentchange', { detail: color }));
}

async function initHero(hero) {
  if (hero.dataset.ready) return;
  hero.dataset.ready = 'true';
  const data = JSON.parse(hero.querySelector('[data-booklets]')?.textContent || '[]');
  const canvas = hero.querySelector('.hero__booklet');
  const nameEl = hero.querySelector('[data-booklet-name]');
  const title = hero.querySelector('.hero__title');

  bounceTitle(hero, title);
  fillMarquee(hero.querySelector('.hero__logos'));
  if (!data.length || !canvas) return;

  // textures à la taille d'affichage : une page fait moins de 400 px à l'écran d'un téléphone, 700 px
  // suffisent (4 fois moins de pixels à télécharger, décoder et envoyer au GPU qu'en 1 200)
  const texWidth = innerWidth < 761 ? 700 : 1200;
  const sized = src => {
    if (!src) return src;
    const url = new URL(src, location.href);
    url.searchParams.set('width', texWidth);
    return url.href;
  };
  const configs = data.map(b => ({ ...BASE, cover: sized(b.cover), backCover: sized(b.backCover), pages: b.pages.map(sized), land: b.land }));
  // le hero s'ouvre sur le dernier livret (le plus récent) ; sa couleur est déjà posée dans le <head>
  let index = data.length - 1, switching = false;
  setAccent(data[index].accent);

  // Three.js n'est chargé que si le hero est là (≈ 110 Ko gzip, préchargé par oqp-hero.liquid)
  const { createBooklet } = await import('@oqp/booklet');
  const booklet = createBooklet(canvas);
  await booklet.load(configs[index]);
  canvas.classList.add('is-ready');   // fondu d'apparition, textures et shaders prêts

  // ouvre le livret quand le hero est à l'écran
  const io = new IntersectionObserver(es => {
    if (es.some(e => e.isIntersecting)) { io.disconnect(); booklet.play(); }
  }, { threshold: .4 });
  io.observe(hero);

  // clic ou toucher sur le livret = fiche du livret affiché (le prototype tournait la page : on ne
  // comprenait pas comment aller au produit). Le bouton « Découvrir » reste le lien accessible au clavier.
  hero.addEventListener('click', e => {
    if (e.target.closest('a, button')) return;
    const url = data[index].url;
    if (!url || url === '#') return;
    if (e.metaKey || e.ctrlKey) open(url, '_blank');
    else location.href = url;
  });

  // ‹ › : le livret sort d'un côté, le suivant entre de l'autre, s'ouvre, et le site prend sa couleur
  async function switchBy(dir) {
    if (switching || data.length < 2) return;
    switching = true;
    index = (index + dir + data.length) % data.length;
    const b = data[index];
    nameEl.querySelector('[data-booklet-label]').textContent = b.name;
    nameEl.href = b.url;
    setTimeout(() => setAccent(b.accent), 450);
    await booklet.switchTo(configs[index], -dir);
    switching = false;
  }
  hero.querySelector('[data-booklet-next]')?.addEventListener('click', () => switchBy(1));
  hero.querySelector('[data-booklet-prev]')?.addEventListener('click', () => switchBy(-1));
}

// Bandeau de logos sans fin : assez de copies pour toujours couvrir l'écran, la boucle avance
// d'exactement un jeu de logos (pas de trou avant qu'elle recommence)
function fillMarquee(track) {
  if (!track) return;
  const set = [...track.querySelectorAll(':scope > img')];
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  const setWidth = () => set.reduce((w, img) => w + img.getBoundingClientRect().width + gap, 0);
  function build() {
    track.querySelectorAll('.hero__logos-copy').forEach(d => d.remove());
    const w = setWidth();
    if (!w) return;
    const copies = Math.ceil(innerWidth / w) + 1;
    for (let i = 0; i < copies; i++) {
      const copy = document.createElement('span');
      copy.className = 'hero__logos-copy';
      copy.setAttribute('aria-hidden', 'true');
      set.forEach(img => { const c = img.cloneNode(); c.alt = ''; c.loading = 'eager'; copy.appendChild(c); });
      track.appendChild(copy);
    }
    track.style.setProperty('--set', `${w}px`);
    track.style.setProperty('--speed', `${(w / 30).toFixed(1)}s`);   // ~30 px par seconde quelle que soit la largeur
  }
  Promise.all(set.map(img => img.decode().catch(() => {}))).then(build);
  let t; addEventListener('resize', () => { clearTimeout(t); t = setTimeout(build, 200); });
}

// Rebond façon logo DVD dans le hero
function bounceTitle(hero, el) {
  if (!el) return;
  let x = Math.min(109, hero.clientWidth * .075), y = Math.min(144, hero.clientHeight * .17), vx = 70, vy = 55;   // px par seconde
  let last = performance.now(), visible = true;
  new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(hero);
  const place = () => { el.style.transform = `translate3d(${x}px, ${y}px, 0)`; };
  if (reduced) return place();
  (function frame(t) {
    requestAnimationFrame(frame);
    const dt = Math.min(.05, (t - last) / 1000); last = t;
    if (!visible) return;
    const maxX = hero.clientWidth - el.offsetWidth, maxY = hero.clientHeight - el.offsetHeight;
    x += vx * dt; y += vy * dt;
    if (x <= 0) { x = 0; vx = Math.abs(vx); } else if (x >= maxX) { x = Math.max(0, maxX); vx = -Math.abs(vx); }
    if (y <= 0) { y = 0; vy = Math.abs(vy); } else if (y >= maxY) { y = Math.max(0, maxY); vy = -Math.abs(vy); }
    place();
  })(performance.now());
}

document.querySelectorAll('.hero[data-hero]').forEach(initHero);
document.addEventListener('shopify:section:load', e => e.target.querySelectorAll('.hero[data-hero]').forEach(initHero));
